import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import { useStores } from "../../../use-stores";
import { CampaignListItem } from "../../../../../models/Campaigns/Campaign";
import { CampaignStatusTab } from "../../../stores/CampaignStore";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import Grid2 from "@mui/material/Grid2";

const getCampaignTypeLabel = (row: CampaignListItem) => {
  if (row.campaign_type?.trim()) return row.campaign_type;
  return "-";
};

const CampaignList: React.FC = () => {
  const theme = useTheme();
  const { campaignStore, userStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitializedFromQuery, setIsInitializedFromQuery] = useState(false);
  const appliedSearchValue = campaignStore.appliedSearch;
  const searchInputValue = campaignStore.searchInput;
  const canViewCampaigns = userStore.hasPermission([
    EmployeeRoles.VIEW_CAMPAIGNS,
  ]);

  useEffect(() => {
    if (!canViewCampaigns) return;
    const queryParams = new URLSearchParams(location.search);
    const search = queryParams.get("search") || "";
    const pageNo = Number(
      queryParams.get("page_no") || queryParams.get("pageNo") || "1",
    );
    const limit = Number(queryParams.get("limit") || "10");
    const statusTab = queryParams.get("statusTab") || "active";

    campaignStore.initializeFromQuery(search, pageNo, limit, statusTab);
    setIsInitializedFromQuery(true);
  }, [campaignStore, location.search, canViewCampaigns]);

  useEffect(() => {
    if (!isInitializedFromQuery || !canViewCampaigns) return;

    campaignStore.getCampaignListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitializedFromQuery,
    canViewCampaigns,
    campaignStore.pageNo,
    campaignStore.limit,
    campaignStore.statusTab,
    appliedSearchValue,
  ]);

  useEffect(() => {
    if (!isInitializedFromQuery || !canViewCampaigns) return;

    const nextQueryParams = new URLSearchParams();
    nextQueryParams.set("page_no", String(campaignStore.pageNo));
    nextQueryParams.set("limit", String(campaignStore.limit));
    nextQueryParams.set("statusTab", campaignStore.statusTab);
    if (appliedSearchValue.trim()) {
      nextQueryParams.set("search", appliedSearchValue.trim());
    }

    const nextSearch = `?${nextQueryParams.toString()}`;
    if (nextSearch !== location.search) {
      navigate({ search: nextQueryParams.toString() }, { replace: true });
    }
  }, [
    isInitializedFromQuery,
    canViewCampaigns,
    campaignStore.pageNo,
    campaignStore.limit,
    campaignStore.statusTab,
    appliedSearchValue,
    location.search,
    navigate,
  ]);

  const handleApplyFilters = () => {
    campaignStore.applyFilters();
  };

  useEffect(() => {
    if (!isInitializedFromQuery || !canViewCampaigns) return;

    const nextSearch = (searchInputValue || "").trim();
    const appliedSearch = (appliedSearchValue || "").trim();
    if (nextSearch === appliedSearch) return;

    const timer = setTimeout(() => {
      campaignStore.applyFilters();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isInitializedFromQuery,
    canViewCampaigns,
    searchInputValue,
    appliedSearchValue,
    campaignStore,
  ]);

  const handleSearchInputChange = (value: string) => {
    campaignStore.setSearchInput(value);
  };

  const onStatusTabChange = async (tab: CampaignStatusTab) => {
    campaignStore.setStatusTab(tab);
  };

  if (!canViewCampaigns) {
    return <PermissionBlockLabel title="Campaigns" />;
  }

  return (
   <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <CustomTypography variant="heading2" fontWeight={700}>
              Campaign Listing
            </CustomTypography>
            <CustomTypography variant="body2" color={theme.palette.text.secondary} sx={{ mt: 0.5 }}>
              List of campaigns in the system.
            </CustomTypography>
          </Box>
          <Paper
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2, borderColor: "divider", overflow: "hidden" }}
          >
            <Box
              onClick={campaignStore.toggleFilters}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                  borderBottom: campaignStore.isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Search & Filters
              </CustomTypography>
                <IconButton size="small">
                  {campaignStore.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
              <Collapse in={campaignStore.isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 360 }}>
                    <FormTextField
                      id="campaign-search"
                      placeholder="Search by campaign name / campaign id"
                      type="search"
                      variant="outlined"
                      sx={{ mb: 0 }}
                        value={searchInputValue}
                        onChange={(_, value) =>
                          handleSearchInputChange(value || "")
                        }
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={handleApplyFilters}
                    customStyles={{ minWidth: 120, height: 40 }}
                  />
                </Box>
              </Box>
            </Collapse>
          </Paper>
          <Tabs
            value={campaignStore.statusTab}
            onChange={(_, value) => onStatusTabChange(value as CampaignStatusTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={(t) => ({ ...listingStatusTabsStyles(t), mb: 2 })}
          >
            <Tab value="active" label="Active" />
            <Tab value="paused" label="Paused" />
            <Tab value="expired" label="Expired" />
          </Tabs>
          {campaignStore.errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {campaignStore.errorMessage}
            </Alert>
          )}

          {campaignStore.isLoading ? (
            <Box
              sx={{
                minHeight: "35vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              sx={{
                marginTop: "0.6rem",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor ||
                          theme.palette.action.hover,
                        "& th": { py: 1, height: 48 },
                      }}
                    >
                      <AppTableHeaderCell
                        title="Campaign ID"
                        fontSize="12px"
                        sx={{ minWidth: 120 }}
                      />
                      <AppTableHeaderCell
                        title="Campaign Name"
                        fontSize="12px"
                        sx={{ minWidth: 220 }}
                      />
                      <AppTableHeaderCell
                        title="Campaign Type"
                        fontSize="12px"
                        sx={{ minWidth: 160 }}
                      />
                      <AppTableHeaderCell
                        title="Created By"
                        fontSize="12px"
                        sx={{ minWidth: 180 }}
                      />
                      <AppTableHeaderCell
                        title="Start Date"
                        fontSize="12px"
                        sx={{ minWidth: 130 }}
                      />
                      <AppTableHeaderCell
                        title="End Date"
                        fontSize="12px"
                        sx={{ minWidth: 130 }}
                      />
                      <AppTableHeaderCell
                        title="Gifts Sent"
                        fontSize="12px"
                        sx={{ minWidth: 120 }}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignStore.rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
                          "& td": { fontSize: "12px", py: 1, height: 48, lineHeight: 1.2 },
                        }}
                      >
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          <Link
                            component={RouterLink}
                            to={`/campaigns/eligible-employees?campaign_id=${row.id}`}
                            underline="always"
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "primary.main",
                            }}
                          >
                            {row.id}
                          </Link>
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {row.name || "-"}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCampaignTypeLabel(row)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {row.created_by_username || "-"}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {formatDateWithoutTime(row.start_date)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {formatDateWithoutTime(row.end_date)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {row.gifts_sent ?? 0}
                        </AppTableRowCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {campaignStore.rows.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    p: 4,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor:
                      theme.palette.customColors?.grayBackGroundColor ||
                      theme.palette.action.hover,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={600}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No campaigns found.
                  </CustomTypography>
                </Box>
              )}

              <AppTablePagenation
                fontSize="12px"
                  totalCount={campaignStore.totalCount}
                  limit={campaignStore.limit}
                  pageNo={campaignStore.pageNo}
                  onPageChange={(_, newPage) => campaignStore.setPageNo(newPage)}
                handleRowsChange={(newLimit) => {
                    campaignStore.setLimit(newLimit);
                }}
              />
            </Paper>
          )}
        </Paper>
      </Box>
    </Grid2>
  );
};

export default observer(CampaignList);
